import React, { useState } from 'react';
import Button from '../components/Button';
import { CheckIcon, InfoIcon } from 'lucide-react';
import Wizard from '../components/Wizard';
const plans = [{
  name: 'Performance',
  price: 'Free',
  period: 'to start',
  description: 'Start with a connected store powered by search — perfect for testing AI sales without risk.',
  features: ['Store connected via search', 'Up to 500 conversations/month', '1 AI sales agent channel', 'Basic analytics & reporting', 'Lead collection', 'Standard support', 'Community access', '$1 per successful conversation after 500', '1 month free on all plans'],
  cta: 'Get Started Free'
}, {
  name: 'Business',
  price: '$299',
  period: '/month',
  description: 'Best for growing businesses ready to scale their AI sales efforts.',
  features: ['Store connected via direct API to your catalog', 'Up to 5,000 conversations/month', '3 AI sales agent channels', 'Advanced analytics & insights', 'Brand voice customization', 'Booking appointment feature', 'Fast checkout integration', 'Priority support', '1 month free on all plans'],
  highlighted: true,
  cta: 'Start Free Trial'
}, {
  name: 'Enterprise',
  price: 'Custom',
  period: 'pricing',
  description: 'Enterprise-grade solution for businesses with higher needs and volume.',
  features: ['Store custom-built & API connected', 'Unlimited conversations', 'Unlimited channels', 'Enterprise analytics', 'Custom integrations', 'Booking appointment feature', 'Fast checkout integration', 'Dedicated support team', 'SLA guarantees & multi-region support', '1 month free on all plans'],
  cta: 'Contact Sales'
}];
const additionalFeatures = [{
  category: 'API Calls',
  description: 'Additional API calls beyond your plan limit',
  pricing: '$0.01 per call'
}, {
  category: 'Conversions',
  description: 'When a conversation leads to a purchase',
  pricing: '3% of order value'
}, {
  category: 'Lead Generation',
  description: 'When a conversation captures contact information',
  pricing: '$1 per qualified lead'
}, {
  category: 'Custom Functions',
  description: 'Specialized AI behaviors for your business',
  pricing: 'Custom pricing based on requirements'
}];
const faqs = [{
  question: 'What counts as a conversation?',
  answer: 'A conversation is counted each time your customer interacts with the AI agent. Each session with multiple questions and responses counts as one conversation.'
}, {
  question: 'How does the 1-month free trial work?',
  answer: "All plans include a 1-month free trial. You can cancel anytime during the trial period, and you won't be charged."
}, {
  question: 'Can I upgrade or downgrade my plan?',
  answer: "Yes, you can change your plan at any time. When upgrading, you'll be prorated for the remainder of your billing cycle."
}, {
  question: 'What happens if I exceed my monthly conversation limit?',
  answer: "You'll automatically be billed for additional conversations at the rate specified in your plan. We'll notify you when you reach 80% of your limit."
}];
const PricingPage = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  return <main className="bg-white" data-id="element-3099">
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-id="element-3100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3101">
          <div className="text-center mb-16" data-id="element-3102">
            <h1 className="text-4xl font-bold mb-4" data-id="element-3103">Transparent Pricing</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8" data-id="element-3104">
              Simple pricing that scales with your business. All plans include a
              1-month free trial.
            </p>
            <div className="inline-flex items-center bg-gray-100 p-1 rounded-full" data-id="element-3105">
              <button className={`px-4 py-2 rounded-full text-sm ${billingCycle === 'monthly' ? 'bg-white shadow-sm' : 'text-gray-600'}`} onClick={() => setBillingCycle('monthly')} data-id="element-3106">
                Monthly
              </button>
              <button className={`px-4 py-2 rounded-full text-sm ${billingCycle === 'annual' ? 'bg-white shadow-sm' : 'text-gray-600'}`} onClick={() => setBillingCycle('annual')} data-id="element-3107">
                Annual (Save 20%)
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3108">
            {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl p-8 shadow-card border ${plan.highlighted ? 'border-primary' : 'border-gray-100'} relative`} data-id="element-3109">
                {plan.highlighted && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-sm px-4 py-1 rounded-full" data-id="element-3110">
                    Most Popular
                  </div>}
                <h3 className="text-xl font-bold mb-2" data-id="element-3111">{plan.name}</h3>
                <div className="mb-4" data-id="element-3112">
                  <span className="text-3xl font-bold" data-id="element-3113">{plan.price}</span>
                  <span className="text-gray-600" data-id="element-3114">{plan.period}</span>
                </div>
                <p className="text-gray-600 mb-6" data-id="element-3115">{plan.description}</p>
                <ul className="space-y-3 mb-8" data-id="element-3116">
                  {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start" data-id="element-3117">
                      <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" data-id="element-3118" />
                      <span className="ml-3 text-gray-600" data-id="element-3119">{feature}</span>
                    </li>)}
                </ul>
                <Button variant={plan.highlighted ? 'primary' : 'outline'} fullWidth onClick={() => setIsWizardOpen(true)} data-id="element-3120">
                  {plan.cta}
                </Button>
              </div>)}
          </div>
        </div>
      </section>
      <section className="py-16 bg-white border-t border-gray-100" data-id="element-3121">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3122">
          <div className="text-center mb-12" data-id="element-3123">
            <h2 className="text-3xl font-bold mb-4" data-id="element-3124">Additional Usage Fees</h2>
            <p className="text-gray-600 max-w-3xl mx-auto" data-id="element-3125">
              All plans can scale with your needs. Here's how we charge for
              additional usage:
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 mb-12" data-id="element-3126">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3127">
              <thead className="bg-gray-100" data-id="element-3128">
                <tr data-id="element-3129">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700" data-id="element-3130">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700" data-id="element-3131">
                    Description
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-700" data-id="element-3132">
                    Pricing
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3133">
                {additionalFeatures.map((feature, index) => <tr key={index} data-id="element-3134">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3135">
                      {feature.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-3136">
                      {feature.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold" data-id="element-3137">
                      {feature.pricing}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 flex items-start mb-16" data-id="element-3138">
            <InfoIcon className="w-6 h-6 text-blue-500 mr-4 flex-shrink-0 mt-1" data-id="element-3139" />
            <div data-id="element-3140">
              <h3 className="font-semibold text-blue-800 mb-2" data-id="element-3141">
                Custom Pricing Available
              </h3>
              <p className="text-blue-700" data-id="element-3142">
                Need a custom solution? We offer tailored pricing for
                high-volume needs, enterprise integrations, and specialized AI
                functionalities. Contact our sales team for a personalized
                quote.
              </p>
            </div>
          </div>
          <div className="text-center" data-id="element-3143">
            <h2 className="text-3xl font-bold mb-8" data-id="element-3144">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto" data-id="element-3145">
              {faqs.map((faq, index) => <div key={index} className="border-b border-gray-200 pb-6" data-id="element-3146">
                  <h3 className="text-lg font-semibold mb-3" data-id="element-3147">{faq.question}</h3>
                  <p className="text-gray-600" data-id="element-3148">{faq.answer}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-gradient-to-b from-white to-gray-50" data-id="element-3149">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3150">
          <div className="bg-primary/5 rounded-2xl p-8 md:p-12 text-center shadow-card border border-primary/20" data-id="element-3151">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3152">
              Ready to transform your retail experience?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-3153">
              Join forward-thinking retailers using AI to boost sales and
              enhance customer experience. Start your free trial today.
            </p>
            <Button variant="primary" size="lg" onClick={() => setIsWizardOpen(true)} data-id="element-3154">
              Get Started with 1-Month Free Trial
            </Button>
          </div>
        </div>
      </section>
      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-3155" />
    </main>;
};
export default PricingPage;